<script lang="ts">
  // Guide tab per INTERFACE-SPECIFICATION.md §14.
  //
  // Two display modes keyed off the route param:
  //
  //   /guide          → landing. Sectioned list of walkthroughs
  //                     (Common actions / Less common / Awareness)
  //                     filtered to the authenticated user's roles.
  //                     Footer holds the two reference pages
  //                     (glossary, roles-explained).
  //
  //   /guide/<slug>   → specific walkthrough. Renders the markdown
  //                     body via marked. Header carries the title,
  //                     visible-to-roles, estimated time, and a
  //                     back-to-landing link.
  //
  // Markdown rendering trusts the Guide content because every entry
  // is gated by the docs/guide CODEOWNERS rule and validated by
  // tools/validate-guide.mjs in continuous integration. No user
  // input reaches the marked.parse call.

  import { marked } from 'marked';
  import { route, navigate } from '../lib/router.svelte';
  import { identity } from '../lib/identity.svelte';
  import {
    findGuideEntry,
    searchGuide,
    sectionByRoleSet,
    type GuideEntry,
    type GuideSearchHit
  } from '../lib/guide-content';

  const slug = $derived(route.current.params.slug);
  const roles = $derived(identity.roles);

  const sectioned = $derived(sectionByRoleSet(roles));
  const entry = $derived<GuideEntry | undefined>(slug ? findGuideEntry(slug) : undefined);

  // Per INTERFACE-SPECIFICATION.md §14.6, MVP search is a simple
  // case-insensitive keyword match. Results render below the search
  // input on the landing page only; opening a specific walkthrough
  // hides the search affordance until the user goes back.
  let searchQuery = $state<string>('');
  const searchResults = $derived<GuideSearchHit[]>(searchGuide(searchQuery));
  const searchActive = $derived(searchQuery.trim().length > 0);

  const glossaryEntry = $derived(findGuideEntry('glossary'));
  const rolesEntry = $derived(findGuideEntry('roles-explained'));
  const indexEntry = $derived(findGuideEntry('_index'));

  // marked v15 renders synchronously when given a string and no
  // async extensions. The `gfm` option enables tables.
  function render(body: string): string {
    return marked.parse(body, { gfm: true, async: false }) as string;
  }

  function goBack(event: MouseEvent) {
    event.preventDefault();
    navigate('/guide');
  }
</script>

<section class="guide">
  {#if entry}
    <header class="guide__entry-header">
      <a href="/guide" onclick={goBack} class="guide__back">← Back to Guide</a>
      <h2>{entry.frontmatter.title}</h2>
      {#if entry.frontmatter.visible_to_roles && entry.frontmatter.visible_to_roles.length > 0}
        <p class="muted">
          For: {entry.frontmatter.visible_to_roles.join(' · ')}
          {#if entry.frontmatter.estimated_time_minutes}
            · about {entry.frontmatter.estimated_time_minutes} minutes
          {/if}
        </p>
      {/if}
    </header>
    <article class="guide__body">
      {@html render(entry.body)}
    </article>
    {#if entry.frontmatter.related_scenarios && entry.frontmatter.related_scenarios.length > 0}
      <footer class="guide__related muted">
        <strong>Related:</strong>
        {#each entry.frontmatter.related_scenarios as relatedSlug (relatedSlug)}
          {@const related = findGuideEntry(relatedSlug)}
          {#if related}
            <a href="/guide/{related.slug}" class="guide__related-link">{related.frontmatter.title}</a>
          {/if}
        {/each}
      </footer>
    {/if}
  {:else}
    <header class="guide__landing-header">
      <h2>Guide</h2>
      {#if indexEntry}
        <article class="guide__body guide__intro">
          {@html render(indexEntry.body)}
        </article>
      {/if}
      {#if roles.length > 0}
        <p class="muted">
          Filtered to your role: {roles.join(' · ')}
        </p>
      {/if}
      <label class="guide__search">
        <span class="muted">Search</span>
        <input
          type="search"
          bind:value={searchQuery}
          placeholder="How do I revert a rate change?"
          aria-label="Search the Guide"
        />
      </label>
    </header>

    {#if searchActive}
      <section class="guide__section">
        {#if searchResults.length === 0}
          <p class="muted">No walkthroughs matched “{searchQuery}”.</p>
        {:else}
          <h3>{searchResults.length} match{searchResults.length === 1 ? '' : 'es'}</h3>
          <ul class="guide__results">
            {#each searchResults as hit (hit.entry.slug)}
              <li>
                <a href="/guide/{hit.entry.slug}">{hit.entry.frontmatter.title}</a>
                <span class="muted guide__result-tag">{hit.matched}</span>
                <p class="muted guide__result-snippet">{hit.snippet}</p>
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    {:else}
      {#if sectioned.common.length > 0}
        <section class="guide__section">
          <h3>Common actions</h3>
          <ul>
            {#each sectioned.common as item (item.slug)}
              <li>
                <a href="/guide/{item.slug}">{item.frontmatter.title}</a>
                {#if item.frontmatter.estimated_time_minutes}
                  <span class="muted"> · about {item.frontmatter.estimated_time_minutes} minutes</span>
                {/if}
              </li>
            {/each}
          </ul>
        </section>
      {/if}

      {#if sectioned.less_common.length > 0}
        <section class="guide__section">
          <h3>Less common, but still you</h3>
          <ul>
            {#each sectioned.less_common as item (item.slug)}
              <li>
                <a href="/guide/{item.slug}">{item.frontmatter.title}</a>
                {#if item.frontmatter.estimated_time_minutes}
                  <span class="muted"> · about {item.frontmatter.estimated_time_minutes} minutes</span>
                {/if}
              </li>
            {/each}
          </ul>
        </section>
      {/if}

      {#if sectioned.awareness.length > 0}
        <section class="guide__section">
          <h3>Not normally for you (shown for awareness)</h3>
          <ul>
            {#each sectioned.awareness as item (item.slug)}
              <li>
                <a href="/guide/{item.slug}">{item.frontmatter.title}</a>
              </li>
            {/each}
          </ul>
        </section>
      {/if}
    {/if}

    <footer class="guide__landing-footer muted">
      {#if glossaryEntry}
        <a href="/guide/{glossaryEntry.slug}">Glossary</a>
      {/if}
      {#if glossaryEntry && rolesEntry} · {/if}
      {#if rolesEntry}
        <a href="/guide/{rolesEntry.slug}">Roles explained</a>
      {/if}
    </footer>
  {/if}
</section>

<style>
  .guide { display: flex; flex-direction: column; gap: var(--space-4); max-width: 900px; }
  .guide__landing-header h2,
  .guide__entry-header h2 { margin: 0 0 var(--space-2); }
  .guide__landing-header,
  .guide__entry-header { display: flex; flex-direction: column; gap: var(--space-2); }
  .guide__intro { margin-top: var(--space-2); }
  .guide__back { font-size: var(--type-sm); }
  .guide__section { display: flex; flex-direction: column; gap: var(--space-2); }
  .guide__section h3 { margin: 0; font-size: var(--type-md); }
  .guide__section ul { margin: 0; padding-left: var(--space-4); }
  .guide__section li { line-height: 1.6; }
  .guide__body :global(h1) { font-size: var(--type-lg); margin-top: var(--space-4); }
  .guide__body :global(h2) { font-size: var(--type-md); margin-top: var(--space-4); }
  .guide__body :global(p) { line-height: 1.6; }
  .guide__body :global(ul),
  .guide__body :global(ol) { padding-left: var(--space-4); line-height: 1.6; }
  .guide__body :global(code) { font-family: var(--font-mono); background: var(--surface-card); padding: 0 var(--space-1); border-radius: var(--radius-xs); }
  .guide__body :global(table) { border-collapse: collapse; margin: var(--space-3) 0; }
  .guide__body :global(th),
  .guide__body :global(td) { border: 1px solid var(--surface-line-soft); padding: var(--space-1) var(--space-2); text-align: left; vertical-align: top; }
  .guide__related { padding: var(--space-3) 0; border-top: 1px solid var(--surface-line-soft); display: flex; gap: var(--space-3); flex-wrap: wrap; }
  .guide__related-link { color: var(--accent-info); }
  .guide__landing-footer { padding: var(--space-3) 0; border-top: 1px solid var(--surface-line-soft); }
  .guide__search { display: flex; flex-direction: column; gap: var(--space-1); max-width: 480px; }
  .guide__search input {
    padding: var(--space-2);
    font-size: var(--type-sm);
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
    background: var(--surface-bg);
    color: var(--surface-fg);
  }
  .guide__results { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-3); }
  .guide__results li { display: flex; flex-direction: column; gap: var(--space-1); }
  .guide__result-tag { font-size: var(--type-xs); text-transform: uppercase; letter-spacing: 0.05em; }
  .guide__result-snippet { margin: 0; line-height: 1.5; }
</style>
